import { memo, useCallback, useState, type MouseEvent, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Tooltip } from '@mui/material';

import Confetti from '../../components/Confetti';
import Icon from '../../components/Icon';
import TagPill from '../../components/TagPill';
import { notchMask } from '../../components/notch';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { COMMITMENT_LABEL, formatArea, formatTimeRange, timeSlotOf } from '../../shared/taxonomy';
import {
  eventUrl,
  formatLabel,
  hasEndTime,
  localDate,
  venueText,
  type Opportunity,
} from '../../shared/opportunity';
import { Badge, Badges, Chip, accessBadges, canGo, entryWord, moreDatesLabel, shortDate } from './cardFacts';

/**
 * One event, drawn as a paper ticket.
 *
 * Geometry follows `_Dev process/Event card.svg`: 683.82 x 182.17 with a notch
 * radius of 12.6 and the stub perforation at 84.1% of the width. The notches
 * are on all four corners as well as both ends of the perforation.
 *
 * Interaction model (the part that is easy to get wrong):
 *
 *   - The card is an <article>, not a link or a button. Nesting buttons inside
 *     a link is invalid HTML and makes screen readers announce the whole card
 *     as one enormous link.
 *   - The title is a real <a href={eventUrl(event)}>. Screen readers announce a link
 *     with a destination, and ⌘-click / middle-click / "open in new tab" all
 *     work natively. A plain left click is intercepted and opens the detail
 *     sheet instead, so ordinary browsing never spawns a tab.
 *   - Only the ticket opens the callout. The tag rail and the buttons beside it
 *     are the card's too, but a click there is theirs or nothing. The ticket's
 *     click handler ignores anything originating inside an <a> or <button>.
 *   - A modifier-click anywhere on the ticket skips the sheet and goes straight
 *     to the event's page, for people who already know what they want.
 */

const ROW = 'mcw-ticket';
/** Tag rail column width; inline tags keep exactly this width too. */
const TAG_W = 132;
/**
 * Where the stub perforation sits. The stub is `max(104px, 15.9%)`, so the
 * perforation is expressed the same way rather than as a flat percentage —
 * otherwise the notches drift off the dashed line at narrow widths.
 */
const PERFORATION = 'min(84.1%, 100% - 104px)';
const NOTCH_R = 11;

const Row = styled('article')(({ theme }) => ({
  display: 'grid',
  // Fixed, not minmax: every sticky note is the same width as every other,
  // per "TAG ICON REFERENCE - dark+light mode enabled+disabled".
  gridTemplateColumns: `${TAG_W}px minmax(0, 1fr) auto`,
  alignItems: 'stretch',
  gap: 0,
  // The ticket is 683.82 x 182.17 in the design file. Capping the row keeps
  // that proportion and leaves the ample margin the reference shows, rather
  // than letting the card stretch to whatever the viewport allows.
  maxWidth: 880,
  marginBottom: 15,
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
  },
}));

/**
 * The four side buttons, spread down the card with equal gaps: Add at the top,
 * Report at the bottom, Save and Not Interested between. Cards vary in height,
 * so the gaps grow and shrink with the card.
 */
const Actions = styled('div')({
  width: 30,
  alignSelf: 'stretch',
  marginLeft: 8,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 0',
});

/** Tags butt directly against the card's left edge, per the design reference. */
const TagRail = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  gap: 4,
  paddingTop: 10,
  paddingRight: 0,
  alignItems: 'stretch',
  [theme.breakpoints.down('lg')]: { display: 'none' },
}));

/**
 * A tag is also a filter control, so it has to be a real button.
 *
 * `$inline` is the collapsed-rail case: the pill keeps its exact rail width
 * rather than stretching to the card, and it does not slide on hover because it
 * is no longer stuck to an edge.
 */
const TagButton = styled('button', { shouldForwardProp: (p) => p !== '$inline' })<{
  $inline?: boolean;
}>(({ $inline }) => ({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  display: 'block',
  width: $inline ? TAG_W : '100%',
  flex: $inline ? `0 0 ${TAG_W}px` : undefined,
  textAlign: 'left',
  cursor: 'pointer',
  transition: 'transform 180ms ease, filter 180ms ease',
  '&:hover': { filter: 'brightness(1.08)', transform: $inline ? 'none' : 'translateX(-2px)' },
  '&:active': { transform: 'none' },
}));

/**
 * At narrow widths the rail is gone and tags move inside the card. They keep
 * their normal 132px width rather than stretching to fill, and stay rounded on
 * all four corners since they are no longer stuck to an edge.
 */
const InlineTags = styled('div')(({ theme }) => ({
  display: 'none',
  gap: 4,
  marginBottom: 4,
  [theme.breakpoints.down('lg')]: {
    display: 'flex',
    flexWrap: 'wrap',
  },
}));

/** A saved-for-later ticket under the Saved pill, as the phone draws it. */
const SAVED_FILL = '#c4beb8';
/**
 * The marks for the list an event is on, under the Saved pill: the phone card's
 * green (#4a632c) and purple (#47355b) at the same hue and saturation, lightened
 * to HSL 65% so they read on the page rather than on a ticket.
 */
const SCHEDULED_MARK = '#a9c883';
const SAVED_MARK = '#a58ebd';

/**
 * The stub of a card on the schedule, under the Saved pill: torn off at the
 * perforation, as the callout tears it (Sam, Sept 18). Hung from its top
 * corner and swung a little clear of the ticket, not dropped as the
 * callout's is: the card's buttons are 8px to its right and the next card
 * 15px below.
 */
const TORN_STUB = 'translate(3px, 1px) rotate(-2.5deg)';

const Ticket = styled('div', { shouldForwardProp: (p) => p !== '$saved' && p !== '$torn' })<{
  $saved?: boolean;
  /** Torn in two: each half draws its own paper. */
  $torn?: boolean;
}>(({ theme, $saved, $torn }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: `minmax(0, 1fr) minmax(104px, 15.9%)`,
  background: $torn ? 'none' : $saved ? SAVED_FILL : BRAND.parchment,
  color: BRAND.mocha,
  minWidth: 0,
  minHeight: 132,
  // Only the ticket opens the callout: the tag rail beside it filters, and the
  // buttons act. A click between tags opened the event, as if it were the card.
  cursor: 'pointer',
  transition: 'transform 120ms ease, filter 350ms ease',
  ...($torn ? {} : notchMask({ radius: NOTCH_R, perforationAt: PERFORATION })),
  // [`.${ROW}:hover &`]: {
  '&:hover': {
    // transform: 'translateY(-1px)',
    filter: 'brightness(1.06)',
  },
  [theme.breakpoints.down('lg')]: { minHeight: 0 },
}));

const Body = styled('div', { shouldForwardProp: (p) => p !== '$torn' })<{ $torn?: boolean }>(({ $torn }) => ({
  padding: '15px 18px 16px',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  ...($torn && { background: BRAND.parchment, ...notchMask({ radius: NOTCH_R, tornEdge: 'right' }) }),
}));

const TitleLink = styled('a', { shouldForwardProp: (p) => p !== '$long' })<{ $long: boolean }>(
  ({ $long }) => ({
    fontFamily: FONTS.serif,
    fontWeight: 700,
    fontSize: $long ? 17 : 21,
    lineHeight: 1.16,
    letterSpacing: '-0.005em',
    color: BRAND.mocha,
    textDecoration: 'none',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textWrap: 'pretty',
    '&:hover': { textDecoration: 'underline', textUnderlineOffset: 2 },
  }),
);

const MetaLine = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: FONTS.body,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.02em',
  color: BRAND.mocha,
  minWidth: 0,
});

const AreaText = styled('span')({ fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap' });


const VenueNote = styled('span')({
  fontWeight: 500,
  fontStyle: 'italic',
  color: 'rgba(54,47,42,0.6)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Summary = styled('p')({
  margin: '2px 0 0',
  fontFamily: FONTS.body,
  fontSize: 12.5,
  lineHeight: 1.45,
  color: 'rgba(54,47,42,0.82)',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

/** Right-hand stub. */
const Stub = styled('div', { shouldForwardProp: (p) => p !== '$torn' })<{ $torn?: boolean }>(({ $torn }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  padding: '12px 7px 11px',
  textAlign: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 11,
    bottom: 11,
    borderLeft: '1px dashed rgba(54,47,42,0.42)',
  },
  ...($torn && {
    background: BRAND.parchment,
    ...notchMask({ radius: NOTCH_R, tornEdge: 'left' }),
    transform: TORN_STUB,
    transformOrigin: '0 0',
    // The perforation is the torn edge now.
    '&::before': { display: 'none' },
  }),
}));

const IntentIcons = styled('div', { shouldForwardProp: (p) => p !== '$saved' })<{ $saved?: boolean }>(
  ({ $saved }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    // Card-only intent icon color, per the design spec; darkened with a saved
    // ticket so the icons stay visible on it, as on the phone.
    color: $saved ? '#a8a29c' : '#c9c5c1',
    flex: 1,
    minHeight: 0,
  }),
);

const FormatWord = styled('div')({
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: '0.04em',
  color: BRAND.mocha,
  lineHeight: 1.1,
  width: '100%',
});

/** The rule sits below the format word, per Event card.svg. */
const Rule = styled('div')({
  width: '100%',
  borderTop: '1px solid rgba(54,47,42,0.3)',
  margin: '5px 0 3px',
});

const StubLabel = styled('div')({
  fontFamily: FONTS.body,
  fontSize: 6.5,
  fontWeight: 700,
  letterSpacing: '0.14em',
  color: 'rgba(54,47,42,0.55)',
  marginTop: 2,
});

const StubValue = styled('div')({
  fontFamily: FONTS.serif,
  fontSize: 11,
  fontWeight: 700,
  color: BRAND.mocha,
  lineHeight: 1.2,
});

const ActionButton = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(
  ({ $on }) => ({
    position: 'relative',
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 4,
    margin: 0,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    color: $on ? BRAND.parchment : BRAND.inactive,
    transition: 'color 120ms ease, transform 120ms ease',
    '&:hover': { color: BRAND.parchment, transform: 'scale(1.12)' },
    '&:active': { transform: 'scale(0.94)' },
    // Report and Not Interested warn in red rather than brightening.
    '&[data-danger="true"]:hover': { color: '#ef3d23' },
    '&[data-state="hidden"]': { color: '#ef3d23' },
    // Not Interested is unavailable for events on either list.
    '&[aria-disabled="true"]': { opacity: 0.32, cursor: 'default' },
    '&[aria-disabled="true"]:hover': { color: BRAND.inactive, transform: 'none' },
  }),
);

/**
 * The icon inside Add or Save. Under the Saved pill, the one for the list the
 * event is on takes that list's colour and grows by a quarter, so which list
 * holds each event reads at a glance; moving an event between the lists grows
 * one and shrinks the other.
 */
const ActionMark = styled('span', { shouldForwardProp: (p) => p !== '$mark' })<{
  $mark: 'scheduled' | 'saved' | null;
}>(({ $mark }) => ({
  display: 'grid',
  placeItems: 'center',
  transform: $mark ? 'scale(1.25)' : 'none',
  transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1), color 200ms ease',
  ...($mark && { color: $mark === 'scheduled' ? SCHEDULED_MARK : SAVED_MARK }),
}));

/** Beside the time on a card listed with "Show past events" on. */
const PastNote = styled('span')({
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  padding: '2px 7px',
  borderRadius: 999,
  background: 'rgba(54,47,42,0.12)',
  color: BRAND.mocha,
  whiteSpace: 'nowrap',
});

interface EventCardProps {
  event: Opportunity;
  rank: number;
  scheduled: boolean;
  saved: boolean;
  /** Listed under the Saved pill, where the list an event is on is marked in colour. */
  savedView: boolean;
  onOpen: (event: Opportunity, rank: number) => void;
  onToggleSchedule: (id: string) => void;
  onToggleSave: (id: string) => void;
  onReport: (event: Opportunity) => void;
  onFilterTopic: (key: string) => void;
  /** Marked Not Interested; only listed while "Show events marked Not Interested" is on. */
  hidden: boolean;
  /** Already over; only listed while "Show past events" is on. */
  past: boolean;
  onToggleHidden: (id: string) => void;
  /** Later dates of the same series that also match: "+ 11 more dates". */
  moreDates?: number;
  /** Under 18 is on and the listing states no age: "Age not stated". */
  ageWarn?: boolean;
  /** Travel time from home, after the neighborhood (Session 5). */
  travel?: ReactNode;
}

function EventCardBase({
  event,
  rank,
  scheduled,
  saved,
  savedView,
  onOpen,
  onToggleSchedule,
  onToggleSave,
  onReport,
  onFilterTopic,
  hidden,
  past,
  onToggleHidden,
  moreDates = 0,
  ageWarn = false,
  travel,
}: EventCardProps) {
  const badges = accessBadges(event);
  /** Cancelled and full events can't be marked Going; one already Going can still be cleared. */
  const goable = canGo(event) || scheduled;
  const slot = timeSlotOf(event);
  /** Planning to go and not being interested contradict each other. */
  const blocked = !hidden && (scheduled || saved);
  const hideTip = hidden
    ? 'Show on my Explore page again'
    : scheduled
      ? "You're going. Clear Going to hide it."
      : saved
        ? 'Marked Maybe. Clear Maybe to hide it.'
        : 'Not Interested';
  const long = event.name.length > 52;
  // Only under the Saved pill: in the main list a card going onto the schedule
  // is on its way out, and tearing it as it leaves would only be a jolt.
  const torn = savedView && scheduled;
  const area = formatArea(event.neighborhood);
  const [burst, setBurst] = useState(0);

  const handleCardClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if ((e.target as HTMLElement).closest('a, button')) return;
      if (window.getSelection()?.toString()) return;
      if (e.metaKey || e.ctrlKey) {
        window.open(eventUrl(event), '_blank', 'noopener,noreferrer');
        return;
      }
      onOpen(event, rank);
    },
    [event, rank, onOpen],
  );

  const handleTitleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      onOpen(event, rank);
    },
    [event, rank, onOpen],
  );

  const handleSchedule = useCallback(() => {
    if (!scheduled) setBurst((n) => n + 1);
    onToggleSchedule(event.id);
  }, [scheduled, onToggleSchedule, event.id]);

  return (
    <Row
      className={ROW}
      // Read by the results list to keep the user's place across a filter
      // change: which card was in the middle, and what moment it stood for.
      data-event-id={event.id}
      style={hidden ? { opacity: 0.6 } : undefined}
    >
      <TagRail>
        {event.topics.slice(0, 3).map((topic) => (
          <TagButton
            key={topic.key}
            type="button"
            aria-label={`Filter by ${topic.label}`}
            title={`Filter by ${topic.label}`}
            onClick={(e) => {
              e.stopPropagation();
              onFilterTopic(topic.key);
            }}
          >
            <TagPill topic={topic} on size="sm" stuck />
          </TagButton>
        ))}
      </TagRail>

      <Ticket $saved={savedView && saved} $torn={torn} onClick={handleCardClick}>
        <Body $torn={torn}>
          <InlineTags>
            {event.topics.slice(0, 2).map((topic) => (
              <TagButton
                key={topic.key}
                $inline
                type="button"
                aria-label={`Filter by ${topic.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onFilterTopic(topic.key);
                }}
              >
                <TagPill topic={topic} on size="sm" />
              </TagButton>
            ))}
          </InlineTags>

          <TitleLink
            href={eventUrl(event)}
            target="_blank"
            rel="noopener noreferrer"
            $long={long}
            onClick={handleTitleClick}
          >
            {event.name}
          </TitleLink>

          <MetaLine>
            <Icon name={`time:${slot ?? 'morning'}`} size={13} />
            <span style={{ whiteSpace: 'nowrap' }}>
              {shortDate(localDate(event))} · {formatTimeRange(event)}
            </span>
            {past && <PastNote>Past event</PastNote>}
            {event.status === 'cancelled' && <Chip data-kind="cancelled">Cancelled</Chip>}
            {event.status !== 'cancelled' && event.capacityStatus === 'full' && <Chip data-kind="full">Full</Chip>}
            {ageWarn && <Chip data-kind="age">Age not stated</Chip>}
            {!hasEndTime(event) && !event.isAllDay && (
              <Tooltip title="This event hasn't published an end time. We've assumed one hour.">
                <Box component="span" sx={{ fontSize: 10, opacity: 0.55, fontWeight: 600, cursor: 'help' }}>
                  end time not listed
                </Box>
              </Tooltip>
            )}
          </MetaLine>

          <MetaLine>
            <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 13, height: 13 }}>
              <LocationPin />
            </Box>
            {area && <AreaText>{area}</AreaText>}
            {/* Plain text, not a link. The venue used to open Google Maps in a
                new tab, which made a click near the bottom of the card do
                something entirely different from a click anywhere else on it —
                and sent the reader out of the app to find out. The callout has
                the map, and the whole card now opens the callout. */}
            <VenueNote>{venueText(event)}</VenueNote>
            {travel}
          </MetaLine>

          {moreDates > 0 && (
            <MetaLine style={{ fontWeight: 600, color: 'rgba(54,47,42,0.75)' }}>
              <span>{moreDatesLabel(moreDates)}</span>
              {event.seriesLabel && <VenueNote>{event.seriesLabel}</VenueNote>}
            </MetaLine>
          )}

          {badges.length > 0 && (
            <Badges>
              {badges.map((b) => (
                <Badge key={b}>{b}</Badge>
              ))}
            </Badges>
          )}

          {event.summary && <Summary>{event.summary}</Summary>}
        </Body>

        <Stub $torn={torn}>
          <IntentIcons $saved={savedView && saved}>
            {/* A real tooltip rather than the SVG's own <title>, which the
                browser shows on its own schedule — about a second of nothing,
                where every other control here answers immediately. */}
            {event.roles.slice(0, 2).map((role) => (
              <Tooltip key={role.key} title={role.label} placement="top" arrow>
                <Box component="span" sx={{ display: 'flex' }}>
                  <Icon name={`role:${role.key}`} emoji={role.icon} size={46} />
                </Box>
              </Tooltip>
            ))}
          </IntentIcons>
          <FormatWord>{COMMITMENT_LABEL[event.commitment]}</FormatWord>
          <Rule />
          <StubLabel>FORMAT</StubLabel>
          <StubValue>{event.format === 'virtual' ? 'Online' : formatLabel(event.format)}</StubValue>
          <StubLabel>ENTRY</StubLabel>
          <StubValue>{entryWord(event)}</StubValue>
        </Stub>
      </Ticket>

      <Actions>
        {goable && (
        <Tooltip title={scheduled ? 'Going (tap to clear)' : 'Going'} placement="left">
          <ActionButton
            type="button"
            $on={scheduled}
            aria-pressed={scheduled}
            aria-label={scheduled ? 'Going (tap to clear)' : 'Going'}
            onClick={handleSchedule}
          >
            <ActionMark $mark={savedView && scheduled ? 'scheduled' : null}>
              <Icon name={scheduled ? 'ui:scheduleOn' : 'ui:schedule'} size={18} />
            </ActionMark>
            <Confetti trigger={burst} />
          </ActionButton>
        </Tooltip>
        )}

        <Tooltip title={saved ? 'Maybe (tap to clear)' : 'Maybe'} placement="left">
          <ActionButton
            type="button"
            $on={saved}
            aria-pressed={saved}
            aria-label={saved ? 'Maybe (tap to clear)' : 'Maybe'}
            onClick={() => onToggleSave(event.id)}
          >
            <ActionMark $mark={savedView && saved ? 'saved' : null}>
              <Icon name={saved ? 'ui:saveOn' : 'ui:save'} size={15} />
            </ActionMark>
          </ActionButton>
        </Tooltip>

        <Tooltip title={hideTip} placement="left">
          <ActionButton
            type="button"
            data-danger={blocked || hidden ? undefined : 'true'}
            data-state={hidden ? 'hidden' : undefined}
            aria-disabled={blocked || undefined}
            aria-pressed={hidden}
            aria-label={hideTip}
            onClick={() => {
              if (!blocked) onToggleHidden(event.id);
            }}
          >
            <Icon name="ui:notInterested" size={16} />
          </ActionButton>
        </Tooltip>

        <Tooltip title="Report a problem with this listing" placement="left">
          <ActionButton
            type="button"
            data-danger="true"
            aria-label="Report a problem with this listing"
            onClick={() => onReport(event)}
          >
            <Icon name="ui:report" size={16} />
          </ActionButton>
        </Tooltip>
      </Actions>
    </Row>
  );
}

/** Small inline pin; the supplied asset set has no standalone location icon. */
function LocationPin() {
  return (
    <svg viewBox="0 0 24 24" width={13} height={13} aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
      />
    </svg>
  );
}

const EventCard = memo(EventCardBase);
export default EventCard;
